<template>
    <div class="file-upload-container">
        <input
            type="file"
            style="display: none"
            ref="fileInput"
            :multiple="multiple"
            :accept="fileTypesAccept"
            @change="selectFile"
        />

        <el-row>
            <el-col :span="5">
                <v-btn
                    class="el-table-info__btn el-table-info__btn--md el-table-info__btn--primary"
                    style="margin-top: 10px"
                    flat
                    @click.stop="onPickFile"
                >
                    {{ $t(buttonText) }}
                </v-btn>
            </el-col>
            <el-col :span="19">
                <el-row>
                    <el-col
                        :span="4"
                        class="file-placeholder"
                        v-for="(fileItem, idx) in selectedFiles"
                        :key="'selected-' + idx"
                    >
                        <span
                            class="el-icon-circle-close file-placeholder-close-icon"
                            @click.stop="remove(fileItem)"
                        />
                        <span class="el-icon-picture file-placeholder-icon" />
                    </el-col>
                    <viewer
                        v-if="displayFiles && displayFiles.length > 0"
                        :images="displayFiles"
                        :options="viewerOptions"
                    >
                        <el-col
                            :span="4"
                            class="file-placeholder-display-img-wrapper"
                            v-for="(fileItem, idx) in displayFiles"
                            :key="'display-' + idx"
                        >
                            <span
                                class="el-icon-circle-close file-placeholder-close-icon"
                                @click.stop="remove(fileItem)"
                            />
                            <img
                                v-if="fileItem.ImagePath"
                                :key="fileItem.ImageId"
                                :src="fileItem.ImagePath"
                                :title="fileItem.ImageId"
                                @error="imageUrlAlt"
                                class="file-placeholder-display-img"
                            />
                        </el-col>
                    </viewer>
                </el-row>
            </el-col>
        </el-row>
    </div>
</template>
<script>
import 'viewerjs/dist/viewer.css'
import FileUploadMixin from '../../mixins/file-upload-mixin'

export default {
    name: 'stc-file-gallery',
    props: {
        // Already uploaded files
        displayFiles: {
            type: Array,
            default: undefined,
        },
        deleteUrl: {
            type: String,
            default: undefined,
        },
        multiple: {
            type: Boolean,
            default: true,
        },
        fileTypesAccept: {
            type: String,
            default: 'image/jpeg,image/png,application/pdf',
        },
        buttonText: {
            type: String,
            default: 'button.text.newDoc',
        },
    },
    mixins: [FileUploadMixin],
    data() {
        return {
            selectedFiles: [], // Files chosen to upload
            viewerOptions: {
                toolbar: true,
                movable: true,
                zoomable: true,
                scalable: false,
                rotatable: false,
                fullscreen: false,
                title: false,
            },
        }
    },
    created: function () {
        Events.$on('upload-files', (url) => {
            this.doFileUpload(url)
        })

        Events.$on('clear-images', () => {
            this.selectedFiles = []
        })
    },
    beforeDestroy() {
        Events.$off('upload-files')
    },
    methods: {
        onPickFile() {
            this.$refs.fileInput.click()
        },
        selectFile(event) {
            const temp = this.selectedFiles
            temp.push(...event.target.files)
            this.selectedFiles = _.uniqBy(temp, 'name')

            // Check file size
            let isInvalid = false

            for (let i = 0; i < this.selectedFiles.length; i++) {
                if (this.selectedFiles[i].size > 10485760) isInvalid = true
            }

            if (isInvalid) {
                this.$message({
                    type: 'error',
                    showClose: true,
                    message: this.$i18n.i18next.t('message.error.general'),
                })
                this.selectedFiles = []
                return
            }
            this.$emit('current-selected-file-count', this.selectedFiles.length)
            this.$emit('selected-files', this.selectedFiles)
        },
        async doFileUpload(url) {
            try {
                const response = await this.doUpload(url, this.selectedFiles)
                if (response) {
                    Events.$emit('upload-files-success', true)
                } else {
                    console.error('file upload error: ', response)
                }
            } catch (error) {
                console.error('file upload error: ', error)
            }
        },
        async remove(fileItem) {
            if (fileItem.ImageType === 'ContainerLogo') {
                Events.$emit('file-removed', true)
                this.selectedFiles = []
            } else if (fileItem.ImageId) {
                try {
                    await this.$confirm(
                        this.$i18n.i18next.t('message.warning.delete', {
                            section: this.$i18n.i18next.t('text.file'),
                        }),
                        'Warning',
                        {
                            confirmButtonText: this.$i18n.i18next.t(
                                'button.text.confirm'
                            ),
                            cancelButtonText: this.$i18n.i18next.t(
                                'button.text.cancel'
                            ),
                            type: 'warning',
                        }
                    )
                    await this.$http.delete(this.deleteUrl + fileItem.ImageId)
                    this.$message({
                        type: 'success',
                        showClose: true,
                        message: this.$i18n.i18next.t(
                            'message.success.fileDeleted'
                        ),
                    })
                    const index = this.displayFiles.indexOf(fileItem)
                    if (index > -1) {
                        this.displayFiles.splice(index, 1)
                    }
                    this.$forceUpdate()
                    return true
                } catch (e) {
                    return false
                }
            } else {
                const index = this.selectedFiles.indexOf(fileItem)
                if (index > -1) {
                    this.selectedFiles.splice(index, 1)
                }
            }
        },
        imageUrlAlt(event) {
            event.target.src = '/images/icon/image-white-icon.png'
        },
    },
}
</script>
<style>
.file-upload-container {
    margin: 10px;
    max-height: 180px;
    overflow-y: auto;
}
.file-placeholder {
    background: white;
    width: 60px;
    height: 60px;
    margin-left: 8px;
    margin-top: 8px;
}
.file-placeholder-icon {
    width: 100%;
    font-size: 30px;
    text-align: center;
}
.file-placeholder-close-icon {
    font-size: 17px;
    color: red;
    left: 48px;
    top: -5px;
    position: relative;
    cursor: pointer;
    z-index: 5;
}
.file-placeholder-display-img {
    width: 60px;
    height: 60px;
    font-size: 30px;
    text-align: center;
    position: absolute;
    right: 0px;
    cursor: pointer;
}
.file-placeholder-display-img-wrapper {
    position: relative;
    width: 60px;
    height: 60px;
    margin-left: 8px;
    margin-top: 8px;
}
</style>